import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import tiaApontando from '@/assets/images/tia/TIA-APONTANDO.png';
import tiaBoasVindas from '@/assets/images/tia/TIA-BOAS-VINDAS.png';
import tiaComemorando from '@/assets/images/tia/TIA-COMEMORANDO.png';
import tiaCursos from '@/assets/images/tia/TIA-CURSOS.png';
import tiaIdeia from '@/assets/images/tia/TIA-IDEIA.png';
import tiaInstrucoes from '@/assets/images/tia/TIA-INSTRUCOES.png';
import tiaJoinha from '@/assets/images/tia/TIA-JOINHA.png';
import tiaOps from '@/assets/images/tia/TIA-OPS.png';
import tiaPensando from '@/assets/images/tia/TIA-PENSANDO.png';
import tiaProvas from '@/assets/images/tia/TIA-PROVAS.png';
import tiaTrilhaEscolha from '@/assets/images/tia/TIA-TRILHA-ESCOLHA.png';

// Cada PNG enquadra a T.I.A numa escala diferente (meio corpo, joelhos, objetos
// em volta). Pra ela não mudar de tamanho nem pular de lugar a cada troca, cada
// pose é escalada pela largura dos óculos e posicionada pelo centro deles.
// Medidas em px da imagem original: w/h = tamanho do PNG; eyeX/eyeY = centro dos
// óculos; glasses = largura da armação.
const POSES = {
  apontando: { src: tiaApontando, w: 1024, h: 1024, eyeX: 418, eyeY: 338, glasses: 314 },
  'boas-vindas': { src: tiaBoasVindas, w: 1024, h: 1024, eyeX: 411, eyeY: 299, glasses: 318 },
  comemorando: { src: tiaComemorando, w: 680, h: 700, eyeX: 326, eyeY: 242, glasses: 228 },
  cursos: { src: tiaCursos, w: 635, h: 700, eyeX: 208, eyeY: 157, glasses: 222 },
  ideia: { src: tiaIdeia, w: 483, h: 700, eyeX: 180, eyeY: 178, glasses: 259 },
  instrucoes: { src: tiaInstrucoes, w: 535, h: 700, eyeX: 165, eyeY: 156, glasses: 225 },
  joinha: { src: tiaJoinha, w: 645, h: 700, eyeX: 213, eyeY: 170, glasses: 235 },
  ops: { src: tiaOps, w: 417, h: 700, eyeX: 196, eyeY: 185, glasses: 253 },
  pensando: { src: tiaPensando, w: 466, h: 700, eyeX: 162, eyeY: 171, glasses: 258 },
  provas: { src: tiaProvas, w: 487, h: 700, eyeX: 135, eyeY: 144, glasses: 200 },
  'trilha-escolha': { src: tiaTrilhaEscolha, w: 550, h: 700, eyeX: 267, eyeY: 152, glasses: 217 }
};

// Largura dos óculos na tela e onde fica o centro deles dentro do quadro da T.I.A
// (x a partir da esquerda, y a partir da base do quadro azul).
const GLASSES_PX = 58;
const EYE_BOTTOM = 114;
// O quadro da T.I.A cresce até onde a pose vai (objetos ao lado, braços), pra
// nada passar por trás do balão. No mobile fica fixo e o balão pode cobrir.
const SLOT_MIN_WIDTH = 136;
const EYE_X = { default: 64, comemorando: 92 };

const poseStyle = (key, eyeX) => {
  const pose = POSES[key];
  const scale = GLASSES_PX / pose.glasses;
  return {
    width: pose.w * scale,
    height: pose.h * scale,
    left: eyeX - pose.eyeX * scale,
    bottom: EYE_BOTTOM - (pose.h - pose.eyeY) * scale
  };
};

// Fala + pose da T.I.A enquanto monta cada matéria. Genéricas de propósito: as matérias
// vêm da API (qualquer uma, inclusive novas), então a fala só encaixa o nome e as poses
// se alternam pela posição da matéria na lista.
const SUBJECT_LINES = [
  [name => `Separando as melhores aulas de ${name}...`, 'cursos'],
  [name => `Procurando o que você mais precisa revisar em ${name}...`, 'pensando'],
  [name => `Escolhendo os conteúdos de ${name} a dedo...`, 'ideia'],
  [name => `Colocando as aulas de ${name} na ordem certinha...`, 'instrucoes'],
  [name => `Conferindo cada detalhe de ${name}...`, 'apontando'],
  [name => `Caprichando na parte de ${name}...`, 'provas']
];

const subjectLine = (name, index) => {
  const [line, pose] = SUBJECT_LINES[index % SUBJECT_LINES.length];
  return [line(name), pose];
};

// Falas enquanto o caderno "terminou" mas a API ainda não respondeu.
const WAIT_LINES = [
  ['Quase lá! Só conferindo se não esqueci nenhuma questão...', 'pensando'],
  ['Apontando os lápis pra deixar tudo caprichado...', 'instrucoes'],
  ['Ops, uma página dobrou. Já tô desamassando!', 'ops'],
  ['Tive uma ideia: mais uma revisãozinha rápida...', 'ideia']
];

const PURPLE = 'var(--color-secondary, #442B8C)';
const GREEN = '#1F8A5B';
const PAPER_TEXT = '#8A7A52';
const LIST_HEIGHT = 196;
const CONFETTI_COLORS = ['#4B2A8A', '#F08A24', '#1F8A5B', '#E5484D', '#F5C542'];

// Tempo por matéria encolhe conforme a quantidade: a animação toda fica entre ~8 e 12s.
const stepMsOf = n => Math.max(650, Math.min(1900, 7000 / Math.max(n, 1)));

// Etapas: 0 = saudação; 1..n = uma por matéria; n+1 = organizando; n+2 = colando
// adesivos (segura aqui até a API responder); n+3 = pronto. Os dados são de
// mentira — a API cria tudo de uma vez —, só a conclusão real (`serverDone`)
// destrava o fim. Com a API já de volta, as etapas restantes correm rápido.
function useCreationSteps(n, serverDone) {
  const [step, setStep] = useState(0);
  const [wait, setWait] = useState(0);
  const last = n + 3;

  useEffect(() => {
    if (step >= last) return undefined;
    const holding = step === last - 1 && !serverDone;
    let delay;
    if (holding) delay = 2600;
    else if (serverDone) delay = 380;
    else if (step === 0) delay = 1600;
    else if (step >= n) delay = 1500;
    else delay = stepMsOf(n);

    const timer = setTimeout(() => (holding ? setWait(w => w + 1) : setStep(s => s + 1)), delay);
    return () => clearTimeout(timer);
  }, [step, wait, serverDone, n, last]);

  return { step, wait, last };
}

function Confetti() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-[38%]"
    >
      {Array.from({ length: 28 }, (_, i) => {
        const angle = (i / 28) * Math.PI * 2;
        const distance = 120 + (i % 4) * 40;
        return (
          <span
            key={i}
            className="absolute"
            style={{
              width: i % 2 ? 8 : 6,
              height: i % 2 ? 12 : 6,
              borderRadius: i % 3 ? 2 : '50%',
              background: CONFETTI_COLORS[i % 5],
              '--dx': `${Math.cos(angle) * distance}px`,
              '--dy': `${Math.sin(angle) * distance + 60}px`,
              animation: `tia-confetti ${1.1 + (i % 5) * 0.15}s cubic-bezier(.2,.7,.4,1) forwards`
            }}
          />
        );
      })}
    </div>
  );
}

function RowIcon({ done, active }) {
  if (done) {
    return (
      <span
        className="flex h-[22px] w-[22px] items-center justify-center rounded-full text-[13px] font-extrabold text-white"
        style={{ background: GREEN, animation: 'tia-pop .35s ease both' }}
      >
        ✓
      </span>
    );
  }
  if (active) {
    return (
      <span
        className="h-[18px] w-[18px] animate-spin rounded-full border-[3px] border-[#E3DCF3]"
        style={{ borderTopColor: PURPLE }}
      />
    );
  }
  return <span className="h-3.5 w-3.5 rounded-full border-2 border-[#D8D2BF]" />;
}

// Conteúdo do modal enquanto a trilha personalizada é criada (tema lúdico).
// `serverDone` = o POST voltou com sucesso; o erro é tratado por quem chama.
export function CreatingTrilhaLoader({ objectiveName, subjects, serverDone, onStart, onClose }) {
  const n = subjects.length;
  const { step, wait, last } = useCreationSteps(n, serverDone);
  const listRef = useRef(null);

  // Pré-carrega as poses pra troca não piscar.
  useEffect(() => {
    Object.values(POSES).forEach(pose => {
      new Image().src = pose.src;
    });
  }, []);

  const finished = step >= last;
  const waiting = step === last - 1 && !serverDone && wait > 0;

  let message;
  let pose;
  if (step === 0) [message, pose] = ['Oba! Anotei suas escolhas. Bora montar seu caderno!', 'boas-vindas'];
  else if (step <= n) [message, pose] = subjectLine(subjects[step - 1], step - 1);
  else if (step === n + 1) [message, pose] = ['Organizando tudo bloco a bloco, do jeitinho que você gosta...', 'trilha-escolha'];
  else if (waiting) [message, pose] = WAIT_LINES[(wait - 1) % WAIT_LINES.length];
  else if (!finished) [message, pose] = ['Colando os últimos adesivos no seu caderno...', 'joinha'];
  else [message, pose] = ['Prontinho! Seu caderno tá novinho em folha. Bora estudar?', 'comemorando'];

  const imageStyle = poseStyle(pose, EYE_X[pose] ?? EYE_X.default);
  const slotWidth = Math.max(SLOT_MIN_WIDTH, Math.ceil(imageStyle.left + imageStyle.width) + 4);
  const base = Math.round((step / last) * 100);
  const percent = finished ? 100 : waiting ? Math.min(97, base + wait * 2) : base;
  const stepLabel = finished ? 'Trilha criada!' : step === 0 ? 'Preparando...' : step <= n ? `Montando matéria ${step} de ${n}` : 'Finalizando';
  const beat = (step + wait) % 2 ? 'a' : 'b';

  // Lista com mais de 4 matérias rola acompanhando a matéria atual.
  const overflow = n > 4;
  const listMask = !overflow ? 'none' : step <= 2 ? 'linear-gradient(#000 80%, transparent)' : step > n - 1 ? 'linear-gradient(transparent, #000 20%)' : 'linear-gradient(transparent, #000 18%, #000 82%, transparent)';

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const rows = list.querySelectorAll('[data-row]');
    const row = rows[Math.min(Math.max(step - 1, 0), rows.length - 1)];
    const maxY = Math.max(0, list.scrollHeight - LIST_HEIGHT);
    const y = step > rows.length ? maxY : Math.min(maxY, Math.max(0, row ? row.offsetTop - 44 : 0));
    list.style.transform = `translateY(-${y}px)`;
  }, [step]);

  return (
    <div className="relative overflow-hidden">
      {/* T.I.A + balão */}
      <div
        className="mt-16 flex min-h-[118px] items-center gap-3 rounded-[18px] bg-[#EEF2F8] pr-4"
        style={{ clipPath: 'inset(-200px 0 0 0 round 0 0 18px 18px)' }}
      >
        <div
          className="relative w-[136px] flex-none self-stretch sm:w-[var(--tia-slot)]"
          style={{ '--tia-slot': `${slotWidth}px`, transition: 'width .4s ease' }}
        >
          <div
            className="absolute inset-0"
            style={{ transformOrigin: '50% 100%', animation: step === 0 ? 'none' : `tia-hop-${beat} .6s ease` }}
          >
            <img
              src={POSES[pose].src}
              alt=""
              aria-hidden="true"
              className="absolute block max-w-none"
              style={{ ...imageStyle, transformOrigin: '50% 100%', animation: 'tia-bob 3s ease-in-out infinite' }}
            />
          </div>
        </div>
        <div className="relative min-w-0 flex-1 py-4">
          <div
            key={`${step}-${wait}`}
            className="rounded-2xl bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(29,35,64,.06)]"
            style={{ animation: `tia-bubble-${beat} .4s ease both` }}
          >
            <span
              className="block text-xs font-extrabold tracking-[.04em]"
              style={{ color: PURPLE }}
            >
              T.I.A
            </span>
            <p
              aria-live="polite"
              className="m-0 mt-0.5 text-[15px] font-semibold leading-snug text-[#1D2340]"
            >
              {message}
            </p>
            {!finished && (
              <div className="mt-2 flex gap-1">
                {[0, 0.15, 0.3].map(delay => (
                  <span
                    key={delay}
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: PURPLE, animation: `tia-dot 1.2s infinite ${delay}s` }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Caderno com as matérias selecionadas */}
      <div className="relative mt-3.5 rounded-[14px] border border-[#ECE6D4] bg-[#FFFDF6] py-4 pl-11 pr-4 shadow-[0_2px_0_#ECE6D4,0_4px_0_#F6F1E3]">
        <div
          aria-hidden="true"
          className="absolute bottom-3.5 left-3.5 top-3.5 flex flex-col justify-around"
        >
          {Array.from({ length: 6 }, (_, i) => (
            <span
              key={i}
              className="h-3.5 w-3.5 rounded-full border-2 border-[#C9C2AE] bg-white"
            />
          ))}
        </div>
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-9 top-0 w-px bg-[#F1C2C2]"
        />

        <div
          className="flex items-baseline justify-between gap-3 text-[13px]"
          style={{ color: PAPER_TEXT }}
        >
          <span className="font-extrabold uppercase tracking-[.06em]">Minha trilha · {objectiveName}</span>
          <span className="font-bold">
            {Math.min(Math.max(step - 1, 0), n)}/{n}
          </span>
        </div>

        <div
          className="relative mt-2.5 overflow-hidden"
          style={{ maxHeight: LIST_HEIGHT, maskImage: listMask, WebkitMaskImage: listMask }}
        >
          <ul
            ref={listRef}
            className="relative m-0 flex list-none flex-col gap-0.5 p-0 transition-transform duration-500"
            style={{ transitionTimingFunction: 'cubic-bezier(.3,.7,.3,1)' }}
          >
            {subjects.map((name, i) => {
              const done = step > i + 1;
              const active = step === i + 1;
              return (
                <li
                  key={name}
                  data-row
                  className="flex flex-none items-center gap-2.5 border-b border-dashed border-[#E6DFCA] py-2"
                  style={{ animation: 'tia-row-in .4s ease both' }}
                >
                  <span className="flex h-[22px] w-[22px] flex-none items-center justify-center">
                    <RowIcon
                      done={done}
                      active={active}
                    />
                  </span>
                  <div className="relative min-w-0 flex-1">
                    <span
                      className="block text-[15px] transition-colors duration-300"
                      style={{ fontWeight: active ? 800 : 600, color: done ? '#1D2340' : active ? PURPLE : '#A39C88' }}
                    >
                      {name}
                    </span>
                    {active && (
                      <div className="relative mt-0.5 h-3.5">
                        <div
                          className="absolute inset-x-0 top-1.5 h-0.5 rounded-sm"
                          style={{
                            background: `linear-gradient(90deg,#E3DCF3 0,${PURPLE} 50%,#E3DCF3 100%)`,
                            backgroundSize: '200px 2px',
                            animation: 'tia-shine 1.2s linear infinite'
                          }}
                        />
                        <span
                          aria-hidden="true"
                          className="absolute -top-0.5 w-5 text-sm"
                          style={{ animation: 'tia-pencil 1.6s ease-in-out infinite' }}
                        >
                          ✏️
                        </span>
                      </div>
                    )}
                  </div>
                  <span
                    className="flex-none text-xs font-bold"
                    style={{ color: PAPER_TEXT }}
                  >
                    {done ? 'pronto' : active ? 'montando' : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Progresso */}
      <div className="mt-4">
        <div
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-2.5 overflow-hidden rounded-full bg-[#EEEAF7]"
        >
          <div
            className="h-full rounded-full transition-[width] duration-700"
            style={{
              width: `${percent}%`,
              background: 'linear-gradient(90deg,#6A3FC0,#3A1F7A)',
              transitionTimingFunction: 'cubic-bezier(.3,.7,.3,1)'
            }}
          />
        </div>
        <div className="mt-1.5 flex justify-between text-xs font-bold text-[#6B7089]">
          <span>{stepLabel}</span>
          <span>{percent}%</span>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2.5">
        {finished ? (
          <>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-[#E3E5EE] bg-white px-[18px] py-3 text-[15px] font-bold text-[#6B7089] transition-colors hover:bg-neutral-50"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={onStart}
              className="flex items-center gap-2 rounded-full px-[22px] py-3 text-[15px] font-extrabold text-white transition-colors hover:brightness-90"
              style={{ background: 'var(--color-secondary-dark, #2D1A6E)', animation: 'tia-pop .4s ease both' }}
            >
              Começar trilha
              <FiArrowRight className="h-4 w-4" />
            </button>
          </>
        ) : (
          <span className="rounded-full bg-[#9AA3C0] px-5 py-3 text-[15px] font-extrabold text-white">Criando…</span>
        )}
      </div>

      {finished && <Confetti />}
    </div>
  );
}
